// Admin UI for configuring Jump It or Fix It puzzle stations (replaces raw JSON for day-to-day use).
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { copyText } from '../utils/clipboardUtils';
import { printJumpItStationSigns } from '../utils/jumpItStationSignPrint';
import './JumpItOrFixItStationsAdminEditor.css';

const JUMP_IT_SEED_STATIONS = [
  {
    stationId: 'stowa',
    name: 'StowA',
    letter: 'Start',
    questionImage: '/jumpitfixit/stowa_question.jpg',
    prompt: 'Look at this stowed line. Jump it or fix it?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Loading Area', image: '/jumpitfixit/stowa_jump_loadingarea.png' },
    fixSponsor: { name: 'PD', image: '/jumpitfixit/stowa_fix_pd.jpg' },
    correctChoice: 'fix',
    correctMessage: "You're off! Head to the next sponsor for your next clue.",
    wrongMessage: 'Not quite. Backtrack and try again.',
  },
  {
    stationId: 'legstraps',
    name: 'Leg Straps',
    letter: 'C',
    questionImage: '/jumpitfixit/legstraps_question.png',
    prompt: 'Frayed leg strap. Jump it or fix it?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Sunpath', image: '' },
    fixSponsor: { name: 'PD', image: '' },
    correctChoice: 'fix',
    correctMessage: 'C is yours! Head to the next sponsor.',
    wrongMessage: 'Nope. Backtrack and try again.',
  },
  {
    stationId: 'bridle',
    name: 'Bridle',
    letter: 'A',
    questionImage: '/jumpitfixit/bridle_question_pin.png',
    prompt: 'What about this bridle / pin setup?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Apex', image: '' },
    fixSponsor: { name: 'UPT', image: '' },
    correctChoice: 'fix',
    correctMessage: 'A collected!',
    wrongMessage: 'Backtrack.',
  },
  {
    stationId: 'window',
    name: 'Pilot Chute Window',
    letter: 'N',
    questionImage: '/jumpitfixit/window_question.jpg',
    prompt: 'Pilot chute window. Jump it or fix it?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Cypres', image: '/jumpitfixit/window_jump_alti2.png' },
    fixSponsor: { name: 'GSG', image: '/jumpitfixit/window_fix_soundsentri.jpeg' },
    correctChoice: 'fix',
    correctMessage: 'N collected!',
    wrongMessage: 'Backtrack.',
  },
  {
    stationId: 'loop',
    name: 'Loop',
    letter: 'O',
    questionImage: '/jumpitfixit/loop_question.jpg',
    prompt: 'Closing loop. Jump it or fix it?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Manifest', image: '/jumpitfixit/loop_jump_manifest.png' },
    fixSponsor: { name: 'Alti-2', image: '/jumpitfixit/loop_fix_cookie.jpeg' },
    correctChoice: 'fix',
    correctMessage: 'O collected!',
    wrongMessage: 'Backtrack.',
  },
  {
    stationId: 'stowb',
    name: 'StowB',
    letter: 'P',
    questionImage: '/jumpitfixit/stowb_question.jpg',
    prompt: 'Another stowed line. Jump it or fix it?',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Cookie', image: '/jumpitfixit/stowb_jump_squirrel.png' },
    fixSponsor: { name: 'Loading Area', image: '/jumpitfixit/stowb_fix_vigil.jpg' },
    correctChoice: 'fix',
    correctMessage: 'P collected!',
    wrongMessage: 'Backtrack.',
  },
  {
    stationId: 'finish',
    name: 'Housing / Finish',
    letter: 'Y',
    questionImage: '/jumpitfixit/finish_question.png',
    prompt: 'Last one — Y or nothing!',
    jumpLabel: 'Jump it!',
    fixLabel: 'Fix it!',
    jumpSponsor: { name: 'Bombshelter', image: '/jumpitfixit/finish_jump_FMlogo.png' },
    fixSponsor: { name: 'Vigil', image: '/jumpitfixit/finish_jump_FMlogo.png' },
    correctChoice: 'fix',
    correctMessage: 'CANOPY complete!',
    wrongMessage: 'Backtrack.',
  },
];

function normNameKey(n) {
  return (n || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normUrlKey(u) {
  const x = (u || '').trim().toLowerCase();
  if (!x) return '';
  try {
    const noQuery = x.replace(/\?[^#]*(?=#|$)/, '').replace(/#.*$/, '');
    return noQuery.replace(/\/+$/, '');
  } catch {
    return x;
  }
}

function normalizeStation(raw) {
  const s = raw && typeof raw === 'object' ? raw : {};
  // New schema: one sponsor per station (the host) + two destination station IDs.
  // Fall back to legacy jumpSponsor as the host if the new field is empty,
  // so puzzles created under the old schema migrate seamlessly.
  const sponsorBlock = (s.sponsor && typeof s.sponsor === 'object')
    ? s.sponsor
    : (s.jumpSponsor && (s.jumpSponsor.name || s.jumpSponsor.image)
        ? s.jumpSponsor
        : (s.fixSponsor && (s.fixSponsor.name || s.fixSponsor.image) ? s.fixSponsor : {}));
  const sponsorPickKey =
    (s.sponsorPickKey ?? s.jumpSponsorPickKey ?? s.fixSponsorPickKey ?? '').toString().trim();
  return {
    stationId: s.stationId != null ? String(s.stationId) : '',
    name: s.name != null ? String(s.name) : '',
    letter: s.letter != null ? String(s.letter) : '',
    questionImage: s.questionImage != null ? String(s.questionImage) : '',
    prompt: s.prompt != null ? String(s.prompt) : '',
    jumpLabel: s.jumpLabel != null ? String(s.jumpLabel) : 'Jump it!',
    fixLabel: s.fixLabel != null ? String(s.fixLabel) : 'Fix it!',
    correctChoice: s.correctChoice === 'jump' ? 'jump' : 'fix',
    correctMessage: s.correctMessage != null ? String(s.correctMessage) : '',
    wrongMessage: s.wrongMessage != null ? String(s.wrongMessage) : '',
    explanation: s.explanation != null ? String(s.explanation) : '',
    sourceUrl: s.sourceUrl != null ? String(s.sourceUrl) : '',
    sourceLabel: s.sourceLabel != null ? String(s.sourceLabel) : '',
    sponsor: {
      name: sponsorBlock?.name != null ? String(sponsorBlock.name) : '',
      image: sponsorBlock?.image != null ? String(sponsorBlock.image) : '',
    },
    sponsorPickKey,
    jumpDestinationStationId: s.jumpDestinationStationId != null ? String(s.jumpDestinationStationId) : '',
    fixDestinationStationId: s.fixDestinationStationId != null ? String(s.fixDestinationStationId) : '',
  };
}

function validatePickKey(key, sponsors, customs) {
  if (!key || key === 'manual') return false;
  if (key.startsWith('sponsor:')) return sponsors.some((sp) => sp.id === key.slice(8));
  if (key.startsWith('custom:')) return customs.some((c) => c.id === key.slice(7));
  return false;
}

function derivePickKey(block, sponsors, customs) {
  const name = (block?.name || '').trim();
  const img = (block?.image || '').trim();
  if (!name && !img) return 'manual';
  const nk = normNameKey(name);
  const ik = normUrlKey(img);
  const sp = sponsors.find(
    (s) => normNameKey(s.name) === nk && normUrlKey(s.logo || '') === ik && ik !== ''
  );
  if (sp) return `sponsor:${sp.id}`;
  const spLoose = sponsors.find((s) => normNameKey(s.name) === nk);
  if (spLoose && !img && !(spLoose.logo || '').trim()) return `sponsor:${spLoose.id}`;
  const cuLoose = customs.find((c) => normNameKey(c.name) === nk);
  if (cuLoose && !img) return `custom:${cuLoose.id}`;
  return 'manual';
}

// Single host sponsor per station now. `which` arg ignored — kept for call-site safety.
function effectivePickKey(_which, station, sponsors, customs) {
  const stored = (station.sponsorPickKey || '').trim();
  if (stored && validatePickKey(stored, sponsors, customs)) return stored;
  return derivePickKey(station.sponsor, sponsors, customs);
}

export function normalizeJumpItCustomLocation(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  let id = (r.id != null ? String(r.id) : '').trim();
  if (!id && typeof crypto !== 'undefined' && crypto.randomUUID) id = crypto.randomUUID();
  if (!id) id = `loc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id,
    name: r.name != null ? String(r.name) : '',
  };
}

function emptyStation() {
  return normalizeStation({});
}

function uploadSlug(station, index) {
  const raw = (station.stationId || station.name || `station-${index + 1}`).toString();
  const slug = raw.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return (slug || `s${index}`).slice(0, 48);
}

function SponsorSourceSelect({ label, value, sponsors, customs, disabled, loading, onPick, usedKeys }) {
  const sel =
    typeof value === 'string' && (value.startsWith('sponsor:') || value.startsWith('custom:'))
      ? value
      : 'manual';
  const taken = usedKeys instanceof Set ? usedKeys : new Set();
  // A pick is hidden if it's claimed by another slot AND isn't the current slot's own selection.
  const isHidden = (key) => taken.has(key) && key !== sel;
  const visibleSponsors = sponsors.filter((sp) => !isHidden(`sponsor:${sp.id}`));
  const visibleCustoms = customs.filter((c) => !isHidden(`custom:${c.id}`));
  return (
    <label className="jumpit-admin-field jumpit-admin-field--block">
      <span>{label}</span>
      <select
        className="jumpit-admin-sponsor-select"
        value={sel}
        disabled={disabled || loading}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          onPick(v);
        }}
      >
        <option value="" disabled>
          {loading ? 'Loading sponsor directory…' : 'Choose sponsor / location'}
        </option>
        <option value="manual">Custom — edit name and image below</option>
        {visibleSponsors.length ? (
          <optgroup label="Event sponsors">
            {visibleSponsors.map((sp) => (
              <option key={sp.id} value={`sponsor:${sp.id}`}>
                {sp.name?.trim() || sp.id}
              </option>
            ))}
          </optgroup>
        ) : null}
        {visibleCustoms.length ? (
          <optgroup label="Extra locations (this puzzle)">
            {visibleCustoms.map((c) => (
              <option key={c.id} value={`custom:${c.id}`}>
                {c.name.trim() || '(unnamed location)'}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
    </label>
  );
}

function StationImageRow({
  label,
  url,
  onUrlChange,
  eventId,
  stationIndex,
  fileKey,
  disabledHint,
}) {
  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const inputRef = useRef(null);

  const onPick = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      if (inputRef.current) inputRef.current.value = '';
      if (!file || !eventId) return;
      setUploadErr('');
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const path = `jumpItStations/${eventId}/${Date.now()}-s${stationIndex}-${fileKey}.${ext}`;
      setBusy(true);
      try {
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        onUrlChange(downloadUrl);
      } catch (err) {
        setUploadErr(err?.message || 'Upload failed');
      } finally {
        setBusy(false);
      }
    },
    [eventId, fileKey, onUrlChange, stationIndex]
  );

  return (
    <div className="jumpit-admin-img-row">
      <div className="jumpit-admin-img-row-label">{label}</div>
      <div className="jumpit-admin-img-row-body">
        {url ? (
          <div className="jumpit-admin-thumb-wrap">
            <img src={url} alt="" className="jumpit-admin-thumb" />
          </div>
        ) : (
          <div className="jumpit-admin-thumb-placeholder">No image</div>
        )}
        <div className="jumpit-admin-img-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="jumpit-admin-file-input"
            disabled={!eventId || busy}
            onChange={onPick}
          />
          <button
            type="button"
            className="jumpit-admin-btn jumpit-admin-btn--ghost"
            disabled={!eventId || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
          <input
            type="url"
            className="jumpit-admin-url-field"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="Or paste image URL"
          />
        </div>
        {!eventId ? <p className="jumpit-admin-upload-hint">{disabledHint}</p> : null}
        {uploadErr ? (
          <p className="jumpit-admin-upload-hint" style={{ color: '#b91c1c' }}>
            {uploadErr}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Display label for a sponsor pick (sponsor:abc / custom:abc / manual entry).
function pickKeyLabel(key, sponsors, customs, fallbackName) {
  if (!key || key === 'manual') return (fallbackName || '').trim() || '(manual entry)';
  if (key.startsWith('sponsor:')) {
    const sp = sponsors.find((s) => s.id === key.slice(8));
    return sp ? sp.name : '(missing sponsor)';
  }
  if (key.startsWith('custom:')) {
    const cu = customs.find((c) => c.id === key.slice(7));
    return cu ? cu.name : '(missing location)';
  }
  return (fallbackName || '').trim() || '(unknown)';
}

// Each station now has one host sponsor — dedupe across the 7 station slots.
function computeDuplicatePickKeys(stations, sponsors, customs) {
  const counts = new Map();
  for (const s of stations) {
    const k = effectivePickKey(null, s, sponsors, customs);
    if (!k || k === 'manual') continue;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
}

function DestinationSelect({ value, stations, currentStationId, onPick, conflict }) {
  return (
    <label className={`jumpit-admin-field jumpit-admin-field--block ${conflict ? 'is-conflict' : ''}`}>
      <span>Goes to station</span>
      <select
        className="jumpit-admin-sponsor-select"
        value={value || ''}
        onChange={(e) => onPick(e.target.value)}
      >
        <option value="">— pick a station —</option>
        {stations
          .filter((s) => s.stationId && s.stationId !== currentStationId)
          .map((s) => (
            <option key={s.stationId} value={s.stationId}>
              {s.name || s.stationId}
              {s.sponsor?.name ? ` — host: ${s.sponsor.name}` : ''}
            </option>
          ))}
      </select>
    </label>
  );
}

function FlowBranch({ which, station, index, stations, setDestination }) {
  const destId = which === 'jump' ? station.jumpDestinationStationId : station.fixDestinationStationId;
  const dest = stations.find((s) => s.stationId === destId);
  const otherDestId = which === 'jump' ? station.fixDestinationStationId : station.jumpDestinationStationId;
  const conflict = !!destId && destId === otherDestId;
  const correct = (station.correctChoice || '').toLowerCase() === which;
  return (
    <div className={`jumpit-flow-branch jumpit-flow-${which} ${correct ? 'is-correct' : 'is-wrong'} ${conflict ? 'is-duplicate' : ''}`}>
      <div className="jumpit-flow-branch-head">
        {dest?.sponsor?.image
          ? <img src={dest.sponsor.image} alt="" className="jumpit-flow-logo" />
          : <div className="jumpit-flow-logo jumpit-flow-logo-empty">→</div>}
        <div className="jumpit-flow-branch-text">
          <div className="jumpit-flow-sponsor">
            {dest ? (dest.sponsor?.name || dest.name || dest.stationId) : '(no destination set)'}
          </div>
          <div className="jumpit-flow-badge">
            {correct ? '✓ correct path → letter awarded on arrival' : '✗ wrong path → backtrack from there'}
          </div>
          {conflict && (
            <div className="jumpit-flow-dup">⚠ same destination as the other branch — pick different stations</div>
          )}
        </div>
      </div>
      <DestinationSelect
        value={destId}
        stations={stations}
        currentStationId={station.stationId}
        onPick={(v) => setDestination(index, which, v)}
        conflict={conflict}
      />
    </div>
  );
}

function FlowStationCard({
  station, index, sponsors, customs, duplicateKeys,
  sponsorsLoading, applySponsorPick, replaceAt, usedKeys,
}) {
  const key = effectivePickKey(null, station, sponsors, customs);
  const dup = key && key !== 'manual' && duplicateKeys.has(key);
  const block = station.sponsor;
  const correct = (station.correctChoice || '').toLowerCase();
  const onNameChange = (v) => replaceAt(index, {
    sponsor: { ...(block || { name: '', image: '' }), name: v },
    sponsorPickKey: '',
  });
  const onImageChange = (v) => replaceAt(index, {
    sponsor: { ...(block || { name: '', image: '' }), image: v },
    sponsorPickKey: '',
  });
  return (
    <div className={`jumpit-flow-station ${dup ? 'is-duplicate' : ''}`}>
      <div className="jumpit-flow-station-no">#{index + 1}</div>
      <div className="jumpit-flow-station-meta">
        <div className="jumpit-flow-station-name">{station.name || '(unnamed)'}</div>
        <div className="jumpit-flow-station-sub">
          Letter: <strong>{station.letter || '—'}</strong>
          {' · '}Correct: <strong>{correct || '—'}</strong>
        </div>
        {station.prompt && <div className="jumpit-flow-prompt">“{station.prompt}”</div>}
        <div className="jumpit-flow-host">
          <div className="jumpit-flow-host-row">
            {block?.image ? <img src={block.image} alt="" className="jumpit-flow-logo jumpit-flow-host-logo" /> : null}
            <div className="jumpit-flow-host-name">{block?.name ? `Hosted by ${block.name}` : 'No host sponsor set'}</div>
          </div>
          {dup && <div className="jumpit-flow-dup">⚠ host also used at another station</div>}
          <SponsorSourceSelect
            label="Host sponsor"
            sponsors={sponsors}
            customs={customs}
            loading={sponsorsLoading}
            disabled={false}
            value={key}
            usedKeys={usedKeys}
            onPick={(v) => applySponsorPick(index, null, v)}
          />
          <label className="jumpit-flow-field">
            <span>Override host name</span>
            <input type="text" value={block?.name || ''} onChange={(e) => onNameChange(e.target.value)} placeholder="(uses sponsor name)" />
          </label>
          <label className="jumpit-flow-field">
            <span>Override host image URL</span>
            <input type="text" value={block?.image || ''} onChange={(e) => onImageChange(e.target.value)} placeholder="(uses sponsor logo)" />
          </label>
        </div>
      </div>
    </div>
  );
}

function FlowChart({ stations, sponsors, customs, duplicateKeys, sponsorsLoading, applySponsorPick, replaceAt, setDestination }) {
  if (!stations.length) return null;
  const usedKeys = new Set();
  for (const st of stations) {
    const k = effectivePickKey(null, st, sponsors, customs);
    if (k && k !== 'manual') usedKeys.add(k);
  }
  return (
    <div className="jumpit-flow">
      <div className="jumpit-flow-head">
        <div className="jumpit-flow-col-station">Station (host sponsor)</div>
        <div className="jumpit-flow-col-branch">If they think JUMP IT — walks to</div>
        <div className="jumpit-flow-col-branch">If they think FIX IT — walks to</div>
      </div>
      {stations.map((s, i) => (
        <div className="jumpit-flow-row" key={s.stationId || i}>
          <FlowStationCard
            station={s} index={i}
            sponsors={sponsors} customs={customs} duplicateKeys={duplicateKeys}
            sponsorsLoading={sponsorsLoading}
            applySponsorPick={applySponsorPick} replaceAt={replaceAt}
            usedKeys={usedKeys}
          />
          <FlowBranch
            which="jump" station={s} index={i}
            stations={stations} setDestination={setDestination}
          />
          <FlowBranch
            which="fix" station={s} index={i}
            stations={stations} setDestination={setDestination}
          />
        </div>
      ))}
    </div>
  );
}

export default function JumpItOrFixItStationsAdminEditor({
  stations,
  onChange,
  customLocations = [],
  onCustomLocationsChange,
  mergedSponsors = [],
  sponsorsLoading = false,
  publicCode,
  eventId,
  printEventTitle = '',
  printPuzzleTitle = '',
  printFooterLeft = '',
  printFooterRight = '',
  printStripTagline = '',
}) {
  const list = useMemo(
    () => (Array.isArray(stations) ? stations.map(normalizeStation) : []),
    [stations]
  );
  const sponsorOptions = useMemo(
    () =>
      [...mergedSponsors].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
      ),
    [mergedSponsors]
  );
  const customNorm = useMemo(
    () => (Array.isArray(customLocations) ? customLocations.map(normalizeJumpItCustomLocation) : []),
    [customLocations]
  );
  const [tab, setTab] = useState('build');
  const duplicateKeys = useMemo(
    () => computeDuplicatePickKeys(list, sponsorOptions, customNorm),
    [list, sponsorOptions, customNorm]
  );
  const duplicateLabels = useMemo(
    () => [...duplicateKeys].map((k) => pickKeyLabel(k, sponsorOptions, customNorm, '')),
    [duplicateKeys, sponsorOptions, customNorm]
  );
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(Array.isArray(stations) ? stations.map(normalizeStation) : [], null, 2)
  );
  const [jsonError, setJsonError] = useState('');
  const prevTabRef = useRef(null);

  useEffect(() => {
    if (tab === 'json' && prevTabRef.current !== 'json') {
      setJsonText(JSON.stringify(list, null, 2));
      setJsonError('');
    }
    prevTabRef.current = tab;
  }, [tab, list]);

  const replaceAt = (i, patch) => {
    const next = list.map((s, j) => (j === i ? normalizeStation({ ...s, ...patch }) : s));
    onChange(next);
  };

  // Single host sponsor per station. `which` kept for back-compat but ignored.
  const applySponsorPick = (i, _which, pickRaw) => {
    const s = list[i];
    if (!s) return;
    const pick = (pickRaw || '').trim();
    if (!pick || pick === 'manual') {
      replaceAt(i, { sponsorPickKey: '' });
      return;
    }
    if (pick.startsWith('sponsor:')) {
      const id = pick.slice(8);
      const sp = sponsorOptions.find((x) => x.id === id);
      if (!sp) return;
      replaceAt(i, {
        sponsor: { name: (sp.name || '').trim(), image: (sp.logo || '').trim() },
        sponsorPickKey: pick,
      });
      return;
    }
    if (pick.startsWith('custom:')) {
      const id = pick.slice(7);
      const cu = customNorm.find((x) => x.id === id);
      if (!cu) return;
      replaceAt(i, {
        sponsor: { name: (cu.name || '').trim(), image: '' },
        sponsorPickKey: pick,
      });
    }
  };

  const setDestination = (i, which, stationIdValue) => {
    if (which === 'jump') replaceAt(i, { jumpDestinationStationId: stationIdValue || '' });
    else replaceAt(i, { fixDestinationStationId: stationIdValue || '' });
  };

  const patchCustomLocation = (id, patch) => {
    if (typeof onCustomLocationsChange !== 'function') return;
    const next = customNorm.map((c) =>
      c.id === id ? normalizeJumpItCustomLocation({ ...c, ...patch }) : c
    );
    onCustomLocationsChange(next);
  };

  const addCustomLocation = () => {
    if (typeof onCustomLocationsChange !== 'function') return;
    onCustomLocationsChange([...customNorm, normalizeJumpItCustomLocation({ name: '' })]);
  };

  const removeCustomLocation = (id) => {
    if (typeof onCustomLocationsChange !== 'function') return;
    onCustomLocationsChange(customNorm.filter((c) => c.id !== id));
  };

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const removeAt = (i) => {
    onChange(list.filter((_, k) => k !== i));
  };

  const addStation = () => {
    onChange([...list, emptyStation()]);
  };

  const loadSeed = () => {
    onChange(JUMP_IT_SEED_STATIONS.map(normalizeStation));
    if (tab === 'json') {
      setJsonText(JSON.stringify(JUMP_IT_SEED_STATIONS, null, 2));
      setJsonError('');
    }
  };

  const onJsonEdit = (text) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text || '[]');
      if (!Array.isArray(parsed)) {
        setJsonError('Must be a JSON array.');
        return;
      }
      setJsonError('');
      onChange(parsed.map(normalizeStation));
    } catch (err) {
      setJsonError(err.message || 'Invalid JSON');
    }
  };

  const handlePrintStationSigns = () => {
    if (!publicCode?.trim()) return;
    if (!list.length) return;
    const titleBase = (printPuzzleTitle || '').trim() || 'Jump It or Fix It';
    const stripVenue =
      (printEventTitle || '').trim() || (printPuzzleTitle || '').trim() || 'Bonus puzzle';
    const ftLeft = (printFooterLeft || '').trim() || 'Fresh Meet';
    const ftRight = (printFooterRight || '').trim() || 'freshmeet.com';
    const tag = (printStripTagline || '').trim() || 'Scan · Solve · Score';
    printJumpItStationSigns({
      stations: list,
      publicCode: publicCode.trim(),
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      stripVenue,
      stripTagline: tag,
      footerLeft: ftLeft,
      footerRight: ftRight,
      documentTitle: `${titleBase} — station signs`,
    });
  };

  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const stationsMissingId = list.filter((s) => !String(s.stationId || '').trim()).length;
  const stationRows = list.map((s, idx) => {
    const jumpDest = list.find((x) => x.stationId === s.jumpDestinationStationId);
    const fixDest = list.find((x) => x.stationId === s.fixDestinationStationId);
    const destLabel = (d) => (d ? (d.sponsor?.name?.trim() || d.name || d.stationId) : '—');
    return {
      order: idx + 1,
      name: s.name || s.stationId || '(unnamed)',
      stationId: s.stationId || '',
      letter: s.letter || '—',
      host: (s.sponsor?.name || '').trim() || '—',
      correct: (s.correctChoice || '').toLowerCase(),
      jumpDestLabel: destLabel(jumpDest),
      fixDestLabel: destLabel(fixDest),
      path: publicCode && s.stationId ? `/p/${publicCode}/${s.stationId}` : '',
      fullUrl: publicCode && s.stationId && baseOrigin ? `${baseOrigin}/p/${publicCode}/${s.stationId}` : '',
    };
  });

  const letterTrailText = list
    .filter((s) => s.letter && s.letter !== 'Start')
    .map((s) => s.letter)
    .join('') || '—';

  const uploadHint = 'Select an event in the dropdown at the top of this page to enable uploads.';

  return (
    <div className="jumpit-admin-editor">
      <div className="jumpit-admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`jumpit-admin-tab ${tab === 'build' ? 'is-active' : ''}`}
          onClick={() => setTab('build')}
        >
          Stations
        </button>
        <button
          type="button"
          role="tab"
          className={`jumpit-admin-tab ${tab === 'flow' ? 'is-active' : ''}`}
          onClick={() => setTab('flow')}
        >
          Flow {duplicateKeys.size > 0 && <span className="jumpit-admin-tab-badge">⚠</span>}
        </button>
        <button
          type="button"
          role="tab"
          className={`jumpit-admin-tab ${tab === 'qr' ? 'is-active' : ''}`}
          onClick={() => setTab('qr')}
        >
          QR & URLs
        </button>
        <button
          type="button"
          role="tab"
          className={`jumpit-admin-tab ${tab === 'json' ? 'is-active' : ''}`}
          onClick={() => setTab('json')}
        >
          Raw JSON
        </button>
      </div>

      {tab === 'flow' && (
        <div className="jumpit-admin-panel">
          <p className="form-hint jumpit-admin-intro">
            Visual flow of each station. The badge on each branch shows what the team sees after they tap that
            button; the one marked ✓ is the path that awards the letter.
          </p>
          {duplicateKeys.size > 0 && (
            <div className="jumpit-admin-warning">
              <strong>Duplicate sponsors detected:</strong> {duplicateLabels.join(', ')}. Each sponsor / location
              should only appear once across the whole puzzle.
            </div>
          )}
          <FlowChart
            stations={list}
            sponsors={sponsorOptions}
            customs={customNorm}
            duplicateKeys={duplicateKeys}
            sponsorsLoading={sponsorsLoading}
            applySponsorPick={applySponsorPick}
            replaceAt={replaceAt}
            setDestination={setDestination}
          />
        </div>
      )}

      {tab === 'build' && (
        <div className="jumpit-admin-panel">
          <p className="form-hint jumpit-admin-intro">
            Order is how stations appear to you and in exports; names map to physical signage. Teams use the same QR paths
            regardless of order. Wrong answer = retry; correct adds the letter. Letter &quot;Start&quot; does not count
            toward the final word.
          </p>
          {duplicateKeys.size > 0 && (
            <div className="jumpit-admin-warning">
              <strong>Duplicate sponsors:</strong> {duplicateLabels.join(', ')}. Pick a different sponsor for the highlighted slots below.
            </div>
          )}

          {typeof onCustomLocationsChange === 'function' ? (
            <details className="jumpit-admin-settings">
              <summary className="jumpit-admin-settings-summary">Extra sponsor locations (not in directory)</summary>
              <p className="form-hint jumpit-admin-settings-hint">
                Add one-off location labels for this puzzle only (name only — no logo). They appear in the sponsor dropdowns
                together with the event sponsor directory, which still supplies logos when selected.
              </p>
              <div className="jumpit-admin-settings-actions">
                <button type="button" className="jumpit-admin-btn jumpit-admin-btn--ghost" onClick={addCustomLocation}>
                  + Add location
                </button>
              </div>
              {customNorm.length === 0 ? (
                <p className="jumpit-admin-settings-empty">No extra locations yet.</p>
              ) : (
                <div className="jumpit-admin-custom-list">
                  {customNorm.map((loc, ci) => (
                    <div key={loc.id} className="jumpit-admin-custom-card">
                      <div className="jumpit-admin-custom-card-head">
                        <span className="jumpit-admin-custom-title">Extra location {ci + 1}</span>
                        <button
                          type="button"
                          className="jumpit-admin-btn jumpit-admin-btn--danger jumpit-admin-btn--sm"
                          onClick={() => removeCustomLocation(loc.id)}
                        >
                          Remove
                        </button>
                      </div>
                      <label className="jumpit-admin-field jumpit-admin-field--block">
                        <span>Display name</span>
                        <input
                          type="text"
                          value={loc.name}
                          onChange={(e) => patchCustomLocation(loc.id, { name: e.target.value })}
                          placeholder="e.g. Manifest tent"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </details>
          ) : null}

          <div className="jumpit-admin-toolbar">
            <button type="button" className="jumpit-admin-btn jumpit-admin-btn--primary" onClick={addStation}>
              + Add station
            </button>
            <button type="button" className="jumpit-admin-btn jumpit-admin-btn--ghost" onClick={loadSeed}>
              Load CANOPY example
            </button>
            <span className="jumpit-admin-count">
              {list.length} station{list.length === 1 ? '' : 's'}
            </span>
          </div>

          {list.length > 0 && (
            <div className="jumpit-admin-chain">
              <div className="jumpit-admin-chain-label">Letter chain (from ordered stations)</div>
              <div className="jumpit-admin-chain-letters" title="Letters awarded (excluding Start)">
                {letterTrailText}
              </div>
              <div className="jumpit-admin-chain-pills" aria-hidden>
                {list.map((s, i) => (
                  <React.Fragment key={`pill-${i}-${s.stationId || i}`}>
                    {i > 0 ? <span className="jumpit-admin-chain-arrow">→</span> : null}
                    <span className="jumpit-admin-pill">
                      <span className="jumpit-admin-pill-order">{i + 1}</span>
                      <span className="jumpit-admin-pill-name">{s.name || s.stationId || 'Station'}</span>
                      <span className="jumpit-admin-pill-letter">{s.letter || '—'}</span>
                      <span
                        className={`jumpit-admin-pill-answer jumpit-admin-pill-answer--${s.correctChoice === 'jump' ? 'jump' : 'fix'}`}
                      >
                        {s.correctChoice === 'jump' ? 'Jump' : 'Fix'}
                      </span>
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="jumpit-admin-cards">
            {list.map((s, i) => (
              <details key={`card-${i}-${s.stationId || 'new'}`} className="jumpit-admin-card" open={i === 0}>
                <summary className="jumpit-admin-card-summary">
                  <span className="jumpit-admin-card-title">
                    {i + 1}. {s.name || s.stationId || 'New station'}
                  </span>
                  <span className="jumpit-admin-card-meta">
                    {s.letter ? `Letter: ${s.letter}` : 'No letter'} · ID: {s.stationId || '—'}
                  </span>
                </summary>
                <div className="jumpit-admin-card-body">
                  <div className="jumpit-admin-field-row">
                    <label className="jumpit-admin-field">
                      <span>Station ID (QR slug)</span>
                      <input
                        type="text"
                        value={s.stationId}
                        onChange={(e) => replaceAt(i, { stationId: e.target.value })}
                        placeholder="e.g. legstraps"
                        spellCheck={false}
                      />
                    </label>
                    <label className="jumpit-admin-field">
                      <span>Display name</span>
                      <input
                        type="text"
                        value={s.name}
                        onChange={(e) => replaceAt(i, { name: e.target.value })}
                        placeholder="Sign / UI name"
                      />
                    </label>
                    <label className="jumpit-admin-field jumpit-admin-field--narrow">
                      <span>Letter</span>
                      <input
                        type="text"
                        value={s.letter}
                        onChange={(e) => replaceAt(i, { letter: e.target.value })}
                        placeholder="C or Start"
                        maxLength={12}
                      />
                    </label>
                  </div>

                  <label className="jumpit-admin-field jumpit-admin-field--block">
                    <span>Prompt</span>
                    <textarea
                      rows={2}
                      value={s.prompt}
                      onChange={(e) => replaceAt(i, { prompt: e.target.value })}
                      placeholder="Shown under the photo"
                    />
                  </label>

                  <div className="jumpit-admin-field-row jumpit-admin-field-row--labels">
                    <label className="jumpit-admin-field">
                      <span>Jump button label</span>
                      <input
                        type="text"
                        value={s.jumpLabel}
                        onChange={(e) => replaceAt(i, { jumpLabel: e.target.value })}
                      />
                    </label>
                    <label className="jumpit-admin-field">
                      <span>Fix button label</span>
                      <input
                        type="text"
                        value={s.fixLabel}
                        onChange={(e) => replaceAt(i, { fixLabel: e.target.value })}
                      />
                    </label>
                  </div>

                  <fieldset className="jumpit-admin-correct">
                    <legend>Correct answer</legend>
                    <label className="jumpit-admin-radio">
                      <input
                        type="radio"
                        name={`jumpit-correct-${i}`}
                        checked={s.correctChoice === 'jump'}
                        onChange={() => replaceAt(i, { correctChoice: 'jump' })}
                      />
                      Jump it
                    </label>
                    <label className="jumpit-admin-radio">
                      <input
                        type="radio"
                        name={`jumpit-correct-${i}`}
                        checked={s.correctChoice === 'fix'}
                        onChange={() => replaceAt(i, { correctChoice: 'fix' })}
                      />
                      Fix it
                    </label>
                  </fieldset>

                  <StationImageRow
                    label="Question photo"
                    url={s.questionImage}
                    onUrlChange={(v) => replaceAt(i, { questionImage: v })}
                    eventId={eventId}
                    stationIndex={i}
                    fileKey={`${uploadSlug(s, i)}-question`}
                    disabledHint={uploadHint}
                  />

                  <p className="form-hint jumpit-admin-sponsor-hint">
                    Host sponsor and destinations (Jump / Fix) live on the <strong>Flow</strong> tab.
                  </p>

                  <div className="jumpit-admin-field-row">
                    <label className="jumpit-admin-field jumpit-admin-field--block">
                      <span>Message when correct</span>
                      <textarea
                        rows={2}
                        value={s.correctMessage}
                        onChange={(e) => replaceAt(i, { correctMessage: e.target.value })}
                      />
                    </label>
                    <label className="jumpit-admin-field jumpit-admin-field--block">
                      <span>Message when wrong</span>
                      <textarea
                        rows={2}
                        value={s.wrongMessage}
                        onChange={(e) => replaceAt(i, { wrongMessage: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="jumpit-admin-card-actions">
                    <button
                      type="button"
                      className="jumpit-admin-btn jumpit-admin-btn--ghost"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      title="Move earlier in chain"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      className="jumpit-admin-btn jumpit-admin-btn--ghost"
                      onClick={() => move(i, 1)}
                      disabled={i >= list.length - 1}
                      title="Move later in chain"
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      className="jumpit-admin-btn jumpit-admin-btn--danger"
                      onClick={() => removeAt(i)}
                    >
                      Remove station
                    </button>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {tab === 'qr' && (
        <div className="jumpit-admin-panel">
          <p className="form-hint">
            Each physical sign encodes one URL. Use <strong>Full URL</strong> when generating QR codes, or the path with
            your site origin.
          </p>
          <div className="jumpit-admin-print-row">
            <button
              type="button"
              className="jumpit-admin-btn jumpit-admin-btn--primary"
              onClick={handlePrintStationSigns}
              disabled={!publicCode?.trim() || !list.length}
            >
              Print letter-size station signs
            </button>
            <span className="jumpit-admin-print-hint">
              Opens one 8.5×11 page per station (template with QR + optional question photo).
            </span>
          </div>
          {stationsMissingId > 0 ? (
            <p className="jumpit-admin-warn">
              {stationsMissingId} station{stationsMissingId === 1 ? '' : 's'} missing a Station ID — QR on those pages will
              be incomplete until you add slugs.
            </p>
          ) : null}
          {!publicCode ? (
            <p className="jumpit-admin-warn">Add a public code to this puzzle to build station URLs.</p>
          ) : (
            <div className="jumpit-admin-table-wrap">
              <table className="jumpit-admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Station</th>
                    <th>Host sponsor</th>
                    <th>Letter</th>
                    <th>Jump → host</th>
                    <th>Fix → host</th>
                    <th>Path</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {stationRows.map((row) => (
                    <tr key={`${row.order}-${row.stationId || 'x'}`}>
                      <td>{row.order}</td>
                      <td>{row.name}</td>
                      <td>{row.host}</td>
                      <td>{row.letter}</td>
                      <td className={row.correct === 'jump' ? 'jumpit-admin-cell-correct' : 'jumpit-admin-cell-wrong'}>
                        {row.jumpDestLabel}
                      </td>
                      <td className={row.correct === 'fix' ? 'jumpit-admin-cell-correct' : 'jumpit-admin-cell-wrong'}>
                        {row.fixDestLabel}
                      </td>
                      <td>
                        <code className="jumpit-admin-code">{row.path || '—'}</code>
                      </td>
                      <td>
                        {row.fullUrl ? (
                          <button
                            type="button"
                            className="jumpit-admin-btn jumpit-admin-btn--sm"
                            onClick={() => copyText(row.fullUrl)}
                          >
                            Copy URL
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'json' && (
        <div className="jumpit-admin-panel">
          <p className="form-hint">Paste or edit the Firestore-shaped array. Invalid JSON is not saved to the form.</p>
          <textarea
            className="jumpit-admin-json"
            rows={14}
            spellCheck={false}
            value={jsonText}
            onChange={(e) => onJsonEdit(e.target.value)}
          />
          {jsonError ? (
            <div className="jumpit-admin-json-err" role="alert">
              {jsonError}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
